import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TextField, Button, Grid, Typography, Container, MenuItem, Select, InputLabel, FormControl, Box, Snackbar, Alert, FormHelperText, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useParams } from 'react-router-dom';
import GenerateMeetingLinkButton from '../GoogleMeet/GenerateMeetingLinkButton';
import { format } from 'date-fns';
import GenerateZoomMeetingButton from '../Zoom/GenerateZoomMeetingButton';
import ZoomAuthButton from '../Zoom/ZoomAuthButton';
import GenerateMicrosoftTeamsLink from '../Microsoft/GenerateMicrosoftTeamsLink';
import ZoomIcon from '@mui/icons-material/ZoomOutMap'; // Just an example icon, replace with actual
import TeamsIcon from '@mui/icons-material/GroupAdd';
import GoogleMeetLogo from './GoogleMeetImage.png';
import ZoomMeetLogo from './ZoomMeetImage.png';
import TeamsMeetLogo from './TeamsMeetImage.png';
import { parseISO } from 'date-fns';



const API_URL = process.env.REACT_APP_API_URL;

const styles = {
  glassBox: {
    backdropFilter: 'blur(15px)',
    backgroundColor: '#000000',
    borderRadius: '16px',
    padding: '2.5rem',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    width: '100%',
    maxWidth: '100%',
    textAlign: 'center',
  },
  logo: {
    width: '160px',
    height: '160px',
    margin: '0 auto 1rem',
  },
  text: {
    color: 'white',
  },
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    width: '100%',
  },
  input: {
    marginBottom: '1rem',
    letterSpacing: 2,
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      transition: 'color 0.3s',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1E90FF',
    },
    '&:hover .MuiInputLabel-root': {
      color: '#1E90FF',
    },
    '& .MuiSelect-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1E90FF',
    },
  },
  dateInput: {
    letterSpacing: 2,
    marginBottom: '1rem',
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'transparent',
      transition: 'color 0.3s',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      transition: 'color 0.3s',
    },
    '& .Mui-focused .MuiInputBase-input::placeholder': {
      color: 'white',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1E90FF',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#1E90FF',
    },
  },
  button: {
    backgroundColor: '#1E90FF',
    color: 'white',
    marginTop: '1.5rem',
  },
};

const CreatePost = ({ userId, onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [maxLimit, setMaxLimit] = useState('');
  const [price, setPrice] = useState(0);
  const [topicName, setTopicName] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subsectionId, setSubsectionId] = useState('');
  const [sections, setSections] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [language, setLanguage] = useState('');
  const [meetingLink, setMeetingLink] = useState("");
  const [googleMeetLink, setGoogleMeetLink] = useState('');
  const [languages, setLanguages] = useState([]);  // State for languages
  const [platform, setPlatform] = useState("");
  const [isValid, setIsValid] = useState(null);

  const { id } = useParams();

  // to track the validity of the link
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const [goPriceless, setGoPriceless] = useState(false);

  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true); // to track check status

  useEffect(() => {
    const checkVerification = async () => {
      if (!userId) return;

      try {
        const response = await axios.post(`${API_URL}/api/check-verification/`, {
          user_id: userId,
        });

        setIsVerified(response.data.status === 'approved');
      } catch (error) {
        console.error('Verification check failed:', error);
        setIsVerified(false);
      } finally {
        setLoading(false);
      }
    };

    checkVerification();
  }, [userId]);




  const validateMeetingLink = (link, platform) => {
    const googleMeetRegex = /https:\/\/meet.google.com\/[a-zA-Z0-9\-]+/;

    const zoomRegex = /https:\/\/\w+\.zoom.us\/j\/\d+(\?pwd=[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?/;  // Updated regex

    const teamsRegex = /https:\/\/teams\.live\.com\/meet\/[a-zA-Z0-9\-]+/;

    switch (platform) {
      case "googleMeet":
        return googleMeetRegex.test(link);
      case "zoom":
        return zoomRegex.test(link);
      case "microsoftTeams":
        return teamsRegex.test(link);
      default:
        return false;
    }
  };





  const handlePlatformChange = (e) => {
    const selectedPlatform = e.target.value;
    setPlatform(selectedPlatform);

    // Validate the link immediately when platform is selected
    if (meetingLink && meetingLink.trim() !== "") {
      const isLinkValid = validateMeetingLink(meetingLink, selectedPlatform);
      setIsValid(isLinkValid);
      if (!isLinkValid) {
        setErrorMessage("Invalid link for the selected platform.");
      } else {
        setErrorMessage("");
      }
    } else {
      // Reset validation when link is empty
      setIsValid(true);
      setErrorMessage("");
    }
  };


  // Handle link change
  const handleLinkChange = (e) => {
    const link = e.target.value;
    setMeetingLink(link);

    // If the link is empty, reset validation and error message
    if (link.trim() === "") {
      setIsValid(true); // Set valid state as true when the link is empty
      setErrorMessage(""); // Reset the error message
    } else if (platform) {
      // Validate the link immediately when user types/pastes it
      const isLinkValid = validateMeetingLink(link, platform);
      setIsValid(isLinkValid);
      if (!isLinkValid) {
        setErrorMessage("Invalid link for the selected platform.");
      } else {
        setErrorMessage("");
      }
    }
  };



  useEffect(() => {
    axios.get(`${API_URL}/list_sections_and_subsections/?user_id=${userId}`)
      .then((response) => {
        const transformedSections = Object.entries(response.data.sections).map(([sectionName, sectionData]) => ({
          sectionId: sectionData.section_id,
          sectionName,
          subsections: sectionData.subsections,
        }));
        setSections(transformedSections);


        if (response.data.languages) {
          setLanguages(response.data.languages);
        }
      })
      .catch((error) => {
        console.error('Error fetching sections:', error);
      });
  }, [userId]);


  const showAlert = () => {
    const alertDiv = document.createElement('div');
    alertDiv.textContent = 'Host session created successfully. You can view your sessions under My Sync > Hosted Sessions > My Hosted Sessions.';
    alertDiv.style.position = 'fixed';
    alertDiv.style.top = '10px';
    alertDiv.style.left = '50%';
    alertDiv.style.transform = 'translateX(-50%)';
    alertDiv.style.padding = '10px 20px';
    alertDiv.style.backgroundColor = 'green';
    alertDiv.style.color = 'white';
    alertDiv.style.borderRadius = '5px';
    alertDiv.style.zIndex = '9999';
    alertDiv.style.fontSize = '16px';

    document.body.appendChild(alertDiv);

    setTimeout(() => {
      alertDiv.remove();
    }, 6000);
  };



  const handleSectionChange = (event) => {
    const selectedSectionId = event.target.value;
    setSectionId(selectedSectionId);

    const selectedSection = sections.find(section => section.sectionId === selectedSectionId);
    setSubsections(selectedSection ? selectedSection.subsections : []);
    setSubsectionId('');
  };

  const handleSubsectionChange = (event) => {
    setSubsectionId(event.target.value);
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handlePost = () => {

    const requiredFields = [
      { field: sectionId, fieldName: "Section Name" },
      { field: subsectionId, fieldName: "Subsection Name" },
      { field: topicName, fieldName: "Topic Name" },
      { field: description, fieldName: "Description" },
      { field: language, fieldName: "Language" },
      { field: duration, fieldName: "Duration" },
      { field: maxLimit, fieldName: "Max Limit" },
      { field: price, fieldName: "Price" },
      { field: selectedDate, fieldName: "Select date" },
      { field: startTime, fieldName: "Start Time" },
      { field: meetingLink, fieldName: "Meeting Link" },
      { field: platform, fieldName: "Platform" }
    ];


    for (let { field, fieldName } of requiredFields) {
      if (field === undefined || field === null || field.toString().trim() === "") {
        if (fieldName === "Price" && field === 0) {
          continue; // Skip validation if price is 0
        }
        setError(`${fieldName} is required.`);
        setOpenSnackbar(true);
        return;
      }
    }


    // Validate startTime format (HH:mm) before passing to postData
    if (startTime && !/^\d{2}:\d{2}$/.test(startTime)) {
      setError('Start Time must be in the format HH:mm.');
      setOpenSnackbar(true);
      return;
    }


    // Parsing startTime and setting it with today's date context
    if (startTime) {
      // Combine startTime with today's date to create a full datetime
      const today = new Date();
      const timeParts = startTime.split(':');
      today.setHours(timeParts[0], timeParts[1]);

      // Now use the date-fns `format()` function to format the full datetime
      const formattedStartTime = format(today, "yyyy-MM-dd'T'HH:mm:ss");

      // Use parseISO to parse the formatted startTime into a valid Date object
      const parsedStartTime = parseISO(formattedStartTime);

      // Ensure that the parsed start time is valid
      if (isNaN(parsedStartTime)) {
        setError('Invalid start time.');
        setOpenSnackbar(true);
        return;
      }
    }

    // Validate the duration
    if (duration > 300) {
      setError('Duration cannot exceed 300 minutes.');
      setOpenSnackbar(true);
      return;
    }

    // Validate the maxLimit
    if (maxLimit > 100) {
      setError('Max Limit cannot exceed 100.');
      setOpenSnackbar(true);
      return;
    }

    const selectedSection = sections.find(section => section.sectionId === sectionId);
    const sectionName = selectedSection ? selectedSection.sectionName : '';

    const selectedSubsection = subsections.find(subsection => subsection.id === subsectionId);
    const subsectionName = selectedSubsection ? selectedSubsection.name : '';

    // Step 3: Prepare postData
    const postData = {
      user_id: userId,
      section_id: sectionId,
      section_name: sectionName,
      subsection_id: subsectionId,
      subsection_name: subsectionName,
      startTime: startTime,
      duration: duration,
      selectedDate: selectedDate,
      max_limit: maxLimit,
      price: price,
      topic_name: topicName,
      description: description,
      language: language,
      meeting_link: meetingLink,
      platform: platform
    };


    // Step 4: Make the API request
    axios.post(`${API_URL}/create_post/`, postData).then((response) => {
      showAlert();
      window.location.reload();
    }).catch((error) => {
      console.error('Error posting data:', error);
      if (error.response && error.response.status === 403) {
        setError('You need an active subscription to post.');
        setOpenSnackbar(true);
      } else {
        setError('An error occurred while posting. Please try again later.');
        setOpenSnackbar(true);
      }
    });
  };


  const handleChange = (event) => {
    setLanguage(event.target.value);
  };

  const handleRedirect = () => {
    navigate('/posting-guidelines');  // Redirect to meeting guidelines page
  };



  const handleDurationChange = (e) => {
    setDuration(e.target.value);
  };

  const durationOptions = [];
  for (let i = 30; i <= 300; i += 15) {
    durationOptions.push(i);
  }

  const handleMaxLimitChange = (e) => {
    setMaxLimit(e.target.value);
  };

  const maxLimitOptions = [];
  for (let i = 10; i <= 100; i += 10) {
    maxLimitOptions.push(i);
  }

  const handlePriceChange = (e) => {
    setPrice(e.target.value);
  };

  // Generate price options based on intervals
  const priceOptions = [];

  // Interval of 10 from 0 to 100
  for (let i = 10; i <= 100; i += 10) {
    priceOptions.push(i);
  }

  // Interval of 25 from 100 to 300
  for (let i = 125; i <= 300; i += 25) {
    priceOptions.push(i);
  }

  // Interval of 50 from 300 to 500
  for (let i = 350; i <= 500; i += 50) {
    priceOptions.push(i);
  }


  const handleRadioChange = (event) => {
    const isGoPriceless = event.target.value === "goPriceless";
    setGoPriceless(isGoPriceless);
    if (isGoPriceless) {
      setPrice(0); // Set price to 0 if "Go priceless" is selected
    } else {
      setPrice(''); // Reset the price selection when going back to "Set Price"
    }
  };

  const renderPlatformInfo = () => {
    switch (platform) {
      case 'googleMeet':
        return (
          <Box textAlign="center">
            {/* <GoogleMeetIcon/> */}
            <img src={GoogleMeetLogo} alt="Google Meet" width={100} />
            <Typography variant="h6">Google Meet</Typography>
          </Box>
        );
      case 'zoom':
        return (
          <Box textAlign="center">
            <img src={ZoomMeetLogo} alt="Zoom" width={100} />
            <Typography variant="h6">Zoom</Typography>
          </Box>
        );
      case 'microsoftTeams':
        return (
          <Box textAlign="center">
            <img src={TeamsMeetLogo} alt="Microsoft Teams" width={100} />
            <Typography variant="h6">Microsoft Teams</Typography>
          </Box>
        );
      default:
        return <Typography variant="body1" sx={{ letterSpacing: 2 }}>Select a platform to see details.</Typography>;
    }
  };

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh', color: '#ffffff' }}>

      <Container maxWidth="lg" sx={{
        marginBottom: { xs: '2rem', sm: '3rem', lg: '5rem' },
        padding: '0 2rem',
      }}>


        <Box sx={{
          padding: '2rem',
          borderRadius: '8px',
          backgroundColor: '#000000',
          transition: 'border-color 0.3s ease',
          '&:hover': {
            borderColor: '#1E90FF',
          },
          paddingBottom: '1.5rem',
        }}>
          <Typography variant="h4" component="h1" sx={{
            marginBottom: '1.5rem',
            marginTop: '1.5rem',
            textAlign: 'center',
            letterSpacing: 5,
            color: 'transparent', // Make the text color transparent so that gradient can be applied
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)', // Apply the gradient
            backgroundClip: 'text', // Clip the background to the text
            WebkitBackgroundClip: 'text', // For Safari browser support

          }}>
            Create and Host a New Session
          </Typography>

          <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', letterSpacing: 2 }}>Select Category</InputLabel>
            <Select
              value={sectionId}
              onChange={handleSectionChange}
              label="Select Section"
              sx={{ letterSpacing: 2 }}

            >
              {sections.map((section) => (
                <MenuItem key={section.sectionId} value={section.sectionId} sx={{ letterSpacing: 2 }}>
                  {section.sectionName}
                </MenuItem>
              ))}
            </Select>

          </FormControl>

          {subsections.length > 0 && (
            <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', letterSpacing: 2 }}>Select Subcategory</InputLabel>
              <Select
                value={subsectionId}
                onChange={handleSubsectionChange}
                label="Select Subsection"
                sx={{ letterSpacing: 2 }}
              >
                {subsections.map((subsection) => (
                  <MenuItem key={subsection.id} value={subsection.id} sx={{ letterSpacing: 2 }}>
                    {subsection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Title"
            variant="outlined"
            fullWidth
            value={topicName}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setTopicName(e.target.value);
              }
            }}
            sx={styles.input}
            inputProps={{
              maxLength: 100,
              style: {
                letterSpacing: 2, // for input text
              },
            }}
            InputLabelProps={{
              style: {
                letterSpacing: 2, // for label
              },
            }}
            FormHelperTextProps={{
              style: {
                letterSpacing: 2,
                color: topicName.length > 90 ? 'orange' : 'inherit', // warning color near limit
              },
            }}
            helperText={`${topicName.length}/100 characters`}
          />


          <TextField
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => {
              if (e.target.value.length <= 500) {
                setDescription(e.target.value);
              }
            }}
            sx={styles.input}
            inputProps={{
              maxLength: 500,
              style: {
                letterSpacing: 2,
              },
            }}
            InputLabelProps={{
              style: {
                letterSpacing: 2,
              },
            }}
            FormHelperTextProps={{
              style: {
                letterSpacing: 2,
                color: description.length > 450 ? 'orange' : 'inherit',
              },
            }}
            helperText={`${description.length}/500 characters`}
          />



          <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
            <InputLabel id="language-select-label" sx={{ letterSpacing: 2 }}>Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              onChange={handleChange}
              label="Language"
              sx={{ letterSpacing: 2 }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.id} value={lang.name} sx={{ letterSpacing: 2 }}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>

            {/* Helper Text with color */}
            <FormHelperText sx={{ color: '#9999', letterSpacing: 2 }}>
              Please select the language in which you will be communicating during the session.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth variant="outlined" required>
            <InputLabel id="duration-label" sx={{ letterSpacing: 2 }}>Duration (in minutes)</InputLabel>
            <Select
              labelId="duration-label"
              value={duration}
              onChange={handleDurationChange}
              label="Duration (in minutes)"
              sx={{ letterSpacing: 2 }}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option} value={option} sx={{ letterSpacing: 2 }}>
                  {option} minutes
                </MenuItem>
              ))}
            </Select>
            <FormHelperText sx={{ letterSpacing: 2 }}>
              Please select the duration of the session (Duration cannot exceed 300 minutes)
            </FormHelperText>
          </FormControl>


          <FormControl fullWidth variant="outlined" required>
            <InputLabel id="max-limit-label" sx={{ letterSpacing: 2 }}>Max Limit (in Numbers)</InputLabel>
            <Select
              labelId="max-limit-label"
              value={maxLimit}
              onChange={handleMaxLimitChange}
              label="Max Limit (in Numbers)"
              sx={{ letterSpacing: 2 }}
            >
              {maxLimitOptions.map((option) => (
                <MenuItem key={option} value={option} sx={{ letterSpacing: 2 }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText sx={{ letterSpacing: 2 }}>
              Max limit is the maximum number of users who can book your session, it cannot exceed 100.
            </FormHelperText>
          </FormControl>

          <FormControl component="fieldset" >
            <RadioGroup row value={goPriceless ? "goPriceless" : "setPrice"} onChange={handleRadioChange} sx={{ letterSpacing: 2 }}>
            </RadioGroup>
          </FormControl>


          <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
            <TextField
              label="Select Date"
              type="date"
              variant="outlined"
              fullWidth
              value={selectedDate}
              onChange={handleDateChange}
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                input: { color: '#ffffff' },
                letterSpacing: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#5555',
                  '& fieldset': {
                    borderColor: '#555555',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#aaaaaa',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E90FF',
                }
              }}
            /><FormHelperText sx={{ color: '#aaaaaa', letterSpacing: 2 }}>
              Please select a date for the session.
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth sx={{ marginBottom: '1.5rem' }}>
            <TextField
              label="Start Time"
              type="time"
              variant="outlined"
              fullWidth
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                input: { color: '#ffffff' },
                letterSpacing: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#5555',
                  '& fieldset': {
                    borderColor: '#555555',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#aaaaaa',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E90FF',
                }
              }}
            /><FormHelperText sx={{ color: '#aaaaaa', letterSpacing: 2 }}>
              Please select time for the session.
            </FormHelperText>
          </FormControl>

          <div>
            <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2 }}>
              <InputLabel id="platform-select-label" sx={{ letterSpacing: 2 }}>Select Platform for Live Session</InputLabel>
              <Select
                labelId="platform-select-label"
                value={platform}
                onChange={handlePlatformChange}
                label="Select Platform for Live Session"
                sx={{ letterSpacing: 2 }}
              >
                <MenuItem value="googleMeet" sx={{ letterSpacing: 2 }}>Google Meet</MenuItem>
                <MenuItem value="microsoftTeams" sx={{ letterSpacing: 2 }}>Microsoft Teams</MenuItem>
                <MenuItem value="zoom" sx={{ letterSpacing: 2 }}>Zoom</MenuItem>
              </Select>
            </FormControl>

            {renderPlatformInfo()}
          </div>


          <TextField
            label="Paste Session Link"
            variant="outlined"
            fullWidth
            sx={{ letterSpacing: 2 }}
            value={meetingLink}
            onChange={handleLinkChange}
            error={isValid === false}
            helperText={
              meetingLink.trim() === ""
                ? "Please go to the selected platform, generate the link for session, and paste it here."
                : isValid === false
                  ? errorMessage
                  : ""
            }
          />


          {isValid === true && meetingLink.trim() !== "" && <p style={{ color: "green", letterSpacing: 2 }}>Link is valid!</p>}
          {isValid === false && meetingLink.trim() !== "" && <p style={{ color: "red", letterSpacing: 2 }}>Invalid link for the selected platform.</p>}


          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '16px', // optional spacing
            }}
          >

            <Button
              variant="contained"
              onClick={handlePost}
              disabled={!isVerified || loading}
              sx={{
                backgroundColor: isVerified ? '#1976d2' : '#aaa',
                color: '#fff',
                letterSpacing: 2,
              }}
            >
              Submit
            </Button>

          </Box>

          {!loading && !isVerified && (
            <Typography
              variant="body2"
              color="#1E90FF"
              sx={{ mt: 2, letterSpacing: 4, textAlign: 'center' }}
            >
              Only verified users are allowed to create host sessions
            </Typography>
          )}

        </Box>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            severity="error"
            sx={{ width: '100%' }}
            onClose={() => setOpenSnackbar(false)}
          >
            {error}
          </Alert>
        </Snackbar>

      </Container>

    </div>
  );
};

export default CreatePost;
